import Link from 'next/link'
import { PropsWithChildren } from 'react'
import { MdChevronRight } from 'react-icons/md'

import { Tick } from 'src/components/atoms/Icons/Tick'
import { IconProps } from 'src/components/atoms/Icons/types'
import { Heading3, ParagraphNoSpace } from 'src/components/atoms/Typography'

type StepState = 'default' | 'disabled' | 'complete'

export type FlowStep<TStep> = {
  label: string
  href: string
  icon: React.ComponentType<IconProps>
  value: TStep
  state: (() => StepState) | StepState
}

const DivWrapper = ({
  children,
  className
}: PropsWithChildren<{ className: string }>) => (
  <div className={className}>{children}</div>
)
type FlowStepperProps<TStep> = {
  steps: Array<FlowStep<TStep>>
}
// eslint-disable-next-line react/function-component-definition
export function FlowStepper<TStep>({ steps }: FlowStepperProps<TStep>) {
  const completedSteps = steps.filter(step => {
    const state = typeof step.state === 'function' ? step.state() : step.state
    return state === 'complete'
  })
  const hasCompletedSteps = completedSteps.length > 0
  return (
    <>
      <div className='flex flex-col gap-5 items-center'>
        <Heading3 className='!m-0 !text-[24px] !leading-[30px] !font-bold md:!text-[28px] md:!leading-[39px] md:!font-bold'>
          Get started
        </Heading3>
        <div className='flex flex-col font-medium !text-[14px] md:!text-[16px] !leading-[20px] md:!leading-[24px] items-center'>
          <ParagraphNoSpace className='font-medium text-center'>
            Complete all 3 steps to request native trees for your planting
            project.
          </ParagraphNoSpace>
          {hasCompletedSteps ? (
            <ParagraphNoSpace className='font-bold'>
              {completedSteps.length} of {steps.length} steps complete
            </ParagraphNoSpace>
          ) : null}
        </div>
      </div>
      <div className='flow-stepper'>
        {steps.map((step, idx) => {
          const state =
            typeof step.state === 'function' ? step.state() : step.state
          const Icon = state === 'complete' ? Tick : step.icon
          const StepWrapper =
            state === 'default' || state === 'complete' ? Link : DivWrapper

          return (
            <StepWrapper
              key={`flow_step_${idx}`}
              href={step.href}
              className={`group flow-stepper-step ${state}`}
            >
              <div className='flow-stepper-icon'>
                <Icon className='w-5 h-5' />
              </div>
              <div className='flex-1 flex flex-col'>
                <span
                  className={`text-[14px] leading-[20px] font-medium ${
                    state === 'complete' ? 'text-shark' : 'text-inherit'
                  }`}
                >
                  Step {idx + 1} {state === 'complete' ? 'complete' : ''}
                </span>
                <span
                  className={`text-[16px] leading-[24px] font-semibold ${
                    state === 'complete' ? 'text-charcoal' : 'text-inherit'
                  }`}
                >
                  {step.label}
                </span>
              </div>
              {state === 'default' ? (
                <MdChevronRight
                  size={20}
                  className='transition-all group-hover:translate-x-1'
                />
              ) : null}
            </StepWrapper>
          )
        })}
      </div>
    </>
  )
}
